import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { getTenants, addTenantToUser, logTenantSwitch } from '../../api/backendApi';
import { useAuth } from '../../contexts/AuthContext';
import { readDealerAdminSession } from '../../pages/Admin/dealerAdminSession';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * TenantSelector component for selecting a tenant context for the current user
 *
 * @param {Object} props
 * @param {Function} props.onTenantSelected - Callback when tenant selection completes successfully
 */
const TenantSelector = ({ onTenantSelected }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const { currentUser } = useAuth();
  if (readDealerAdminSession()) {
    return null;
  }

  // Fetch tenants on component mount
  useEffect(() => {
    fetchTenants();
  }, []);

  // Fetch all tenants from the API
  const fetchTenants = async () => {
    try {
      setLoadingTenants(true);
      const tenantsData = await getTenants();
      setTenants(tenantsData);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      toast.error('Failed to load tenant options');
    } finally {
      setLoadingTenants(false);
    }
  };

  // Handle tenant selection and assignment
  const handleTenantSelect = async () => {
    if (!selectedTenantId || !currentUser) return;

    setLoading(true);
    try {
      // Capture previous tenant before making changes
      const previousTenantId =
        localStorage.getItem('selectedTenantId') ||
        localStorage.getItem('selectedRestToolsTenant') ||
        undefined;

      // Save tenant selection to localStorage before API call (legacy key for REST tools)
      localStorage.setItem('selectedRestToolsTenant', selectedTenantId);

      await addTenantToUser(currentUser.uid, selectedTenantId);
      toast.success('Tenant selected successfully');

      // Force token refresh to update claims
      await currentUser.getIdToken(true);

      // Log tenant switch (non-admin v2). Do not block UX on failure.
      try {
        await logTenantSwitch(selectedTenantId, previousTenantId);
      } catch (e) {
        console.warn('TenantSelector: logTenantSwitch failed (non-blocking):', e);
      }

      // Notify parent component that tenant was selected
      if (onTenantSelected) {
        onTenantSelected(selectedTenantId);
      }

      // Force a complete page refresh with query parameter to ensure proper loading
      setTimeout(() => {
        window.location.href =
          window.location.pathname + '?tenant=' + selectedTenantId + '&t=' + Date.now();
      }, 1000); // Give time for the success message to be seen
    } catch (err) {
      console.error('Error selecting tenant:', err);
      toast.error('Failed to select tenant');
      // Remove from localStorage if there was an error
      localStorage.removeItem('selectedRestToolsTenant');
    } finally {
      setLoading(false);
    }
  };

  // Handle tenant selection change
  const handleTenantChange = e => {
    setSelectedTenantId(e.target.value);
  };

  if (loadingTenants) {
    return (
      <div className="flex justify-center items-center p-8">
        <LoadingSpinner text="Loading tenants..." />
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto bg-amber-50 border border-amber-200 rounded-md text-center">
        <h1 className="text-xl font-bold text-amber-700 mb-2">No Tenants Available</h1>
        <p className="text-amber-600">
          There are no tenants available for selection. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto bg-white border border-gray-200 rounded-md shadow">
      <h2 className="text-xl font-semibold mb-4">Select a Tenant</h2>
      <p className="text-gray-600 mb-4">Please select a tenant to access the REST testing tools.</p>

      <div className="mb-4">
        <label htmlFor="tenant-select" className="block text-sm font-medium text-gray-700 mb-1">
          Tenant
        </label>
        <select
          id="tenant-select"
          value={selectedTenantId}
          onChange={handleTenantChange}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          disabled={loading}
        >
          <option value="">-- Select a tenant --</option>
          {tenants.map(tenant => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={handleTenantSelect}
          disabled={!selectedTenantId || loading}
        >
          {loading ? (
            <>
              <LoadingSpinner size={8} className="mr-2" />
              Selecting...
            </>
          ) : (
            'Select Tenant'
          )}
        </button>
      </div>
    </div>
  );
};

export default TenantSelector;
